/**
 * HorizontalBarComparison — multi-row home-vs-away chart.
 *
 * Renamed from `particles/OpposingBars.tsx` (Phase 0b). Animates the
 * bars from 0% on `inView`, and counts the numeric values up alongside
 * via `useCountUp`. Same behaviour as before — no visible change.
 *
 * Shape:
 *   { rows: Array<{ label, homeValue, awayValue, format? }>;
 *     homeLabel: string; awayLabel: string }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { useStaggerChildren } from '../../hooks/useInView.js';
import { useCountUp } from '../../hooks/useCountUp.js';
import styles from './HorizontalBarComparison.module.css';

/**
 * `format` is a TS-only function prop — it lives outside the wire shape
 * since the LLM has no use for inventing formatters. The schema captures
 * label + the two numeric values; consumers attach their own formatter
 * in JSX.
 */
const dataShape = z.object({
  homeLabel: z.string(),
  awayLabel: z.string(),
  rows: z
    .array(
      z.object({
        label: z.string(),
        homeValue: z.number(),
        awayValue: z.number(),
      }),
    )
    .min(1),
});

export const META: PrimitiveMeta = {
  name: 'horizontal-bar-comparison',
  description: 'Multi-row home-vs-away bar chart with sweep + count-up.',
  composable: true,
  dataShape,
  densities: ['standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'bar-grow',
};

export interface HorizontalBarComparisonRow {
  label: string;
  homeValue: number;
  awayValue: number;
  format?: (v: number) => string;
}

export interface HorizontalBarComparisonProps {
  rows: HorizontalBarComparisonRow[];
  homeLabel: string;
  awayLabel: string;
}

interface AnimatedValueProps {
  value: number;
  format?: (v: number) => string;
  enabled: boolean;
}

function AnimatedValue({ value, format, enabled }: AnimatedValueProps): ReactElement {
  const raw = useCountUp({
    end: value,
    duration: 600,
    decimals: value % 1 !== 0 ? 1 : 0,
    enabled,
  });
  if (!enabled) {
    return <>{format ? format(value) : String(value)}</>;
  }
  return <>{format ? format(parseFloat(raw)) : raw}</>;
}

export function HorizontalBarComparison({
  rows,
}: HorizontalBarComparisonProps): ReactElement {
  // #457 (2026-05-23) — homeLabel + awayLabel are still IN the schema
  // for engine back-compat, but no longer rendered. The widget's
  // TeamIdentity row at the top of the parent card carries team
  // labelling; repeating it here was a dupe Johan flagged.
  //
  // Per-row max normalisation (also #457): each row scales bars to
  // ITS OWN bigger value, not the global max across all rows. A row
  // comparing 2.4 vs 1.7 now shows 2.4 as a full bar and 1.7 at ~71%
  // — instead of both shrinking to <12% relative to a 21-vs-10 row
  // higher up. Each row tells its OWN comparison story.

  const { ref, inView, getStaggerStyle } = useStaggerChildren<HTMLDivElement>({
    threshold: 0.1,
    staggerMs: 80,
  });

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.rows}>
        {rows.map((row, index) => {
          // Per-row max — the larger value gets a full bar, the smaller
          // is proportional. Both 0 → both 0% (no degenerate divide).
          const rowMax = Math.max(row.homeValue, row.awayValue);
          const homePercent = rowMax > 0 ? (row.homeValue / rowMax) * 100 : 0;
          const awayPercent = rowMax > 0 ? (row.awayValue / rowMax) * 100 : 0;

          return (
            <div
              key={index}
              className={`${styles.row} stagger-child${inView ? ' visible' : ''}`}
              style={getStaggerStyle(index)}
            >
              {/* Home side — value pinned to outer (left) edge, bar
               *  fills inward toward the center label. */}
              <div className={styles.homeSide}>
                <div className={styles.homeValue}>
                  <AnimatedValue
                    value={row.homeValue}
                    enabled={inView}
                    {...(row.format !== undefined ? { format: row.format } : {})}
                  />
                </div>
                <div className={styles.barWrapper}>
                  <div className={styles.homeBar}>
                    <div
                      className={styles.homeBarFill}
                      style={{
                        width: inView ? `${homePercent}%` : '0%',
                        transition: `width 600ms cubic-bezier(0.16, 1, 0.3, 1) ${index * 80 + 200}ms`,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.label}>{row.label}</div>

              {/* Away side — mirrored via flex-direction: row-reverse.
               *  Value sits on the outer (right) edge, bar fills inward. */}
              <div className={styles.awaySide}>
                <div className={styles.awayValue}>
                  <AnimatedValue
                    value={row.awayValue}
                    enabled={inView}
                    {...(row.format !== undefined ? { format: row.format } : {})}
                  />
                </div>
                <div className={styles.barWrapper}>
                  <div className={styles.awayBar}>
                    <div
                      className={styles.awayBarFill}
                      style={{
                        width: inView ? `${awayPercent}%` : '0%',
                        transition: `width 600ms cubic-bezier(0.16, 1, 0.3, 1) ${index * 80 + 200}ms`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default HorizontalBarComparison;
